import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  Users,
  Shield,
  ShieldOff,
  Trash2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Crown,
  User,
  UserPlus,
  X,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Admin() {
  const { authAxios } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "user" });
  const [creating, setCreating] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authAxios().get("/admin/users");
      setUsers(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement des utilisateurs");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const toggleUser = async (userId, currentStatus) => {
    try {
      await authAxios().put(`/admin/users/${userId}/toggle`);
      toast.success(currentStatus ? "Accès désactivé" : "Accès activé");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const toggleRole = async (userId, currentRole) => {
    const newRole = currentRole === "admin" ? "user" : "admin";
    try {
      await authAxios().put(`/admin/users/${userId}/role?role=${newRole}`);
      toast.success(`Rôle changé en ${newRole}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Supprimer l'utilisateur ${email} ?`)) return;
    try {
      await authAxios().delete(`/admin/users/${userId}`);
      toast.success("Utilisateur supprimé");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await authAxios().post("/admin/users", newUser);
      toast.success("Utilisateur créé");
      setNewUser({ name: "", email: "", password: "", role: "user" });
      setShowCreate(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  const activeCount = users.filter((u) => u.is_active).length;
  const pendingCount = users.filter((u) => !u.is_active).length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Administration</h2>
        <p className="text-slate-500 mt-1">Gérer les utilisateurs et les accès</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-100">
                <Users className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{users.length}</p>
                <p className="text-sm text-slate-500">Total utilisateurs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{activeCount}</p>
                <p className="text-sm text-slate-500">Actifs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <XCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{pendingCount}</p>
                <p className="text-sm text-slate-500">En attente</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create user form */}
      {showCreate && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Créer un utilisateur</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={createUser} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-name">Nom</Label>
                <Input
                  id="new-name"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  placeholder="Nom complet"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-email">Courriel</Label>
                <Input
                  id="new-email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="courriel@exemple.com"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">Mot de passe</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="Minimum 6 caractères"
                  required
                  minLength={6}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-role">Rôle</Label>
                <select
                  id="new-role"
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="user">Standard</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <Button type="submit" disabled={creating}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  {creating ? "Création..." : "Créer l'utilisateur"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Users list */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Utilisateurs</CardTitle>
              <CardDescription>Liste de tous les comptes enregistrés</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {!showCreate && (
                <Button variant="default" size="sm" onClick={() => setShowCreate(true)}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Créer
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={fetchUsers} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                Rafraîchir
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">Chargement...</div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-slate-500">Aucun utilisateur</div>
          ) : (
            <div className="divide-y divide-slate-200">
              {users.map((u) => (
                <div key={u.id} className="flex items-center justify-between py-4">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${
                        u.role === "admin" ? "bg-indigo-600" : "bg-slate-400"
                      }`}
                    >
                      {u.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{u.name}</span>
                        {u.role === "admin" && (
                          <Badge variant="default" className="text-xs bg-indigo-600">
                            Admin
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">{u.email}</p>
                      <p className="text-xs text-slate-400">
                        Inscrit le {new Date(u.created_at).toLocaleDateString("fr-CA")}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Status badge */}
                    <Badge variant={u.is_active ? "default" : "secondary"} className={u.is_active ? "bg-emerald-600" : ""}>
                      {u.is_active ? "Actif" : "Inactif"}
                    </Badge>

                    {/* Toggle active */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleUser(u.id, u.is_active)}
                      title={u.is_active ? "Désactiver" : "Activer"}
                    >
                      {u.is_active ? (
                        <ShieldOff className="w-4 h-4 text-amber-600" />
                      ) : (
                        <Shield className="w-4 h-4 text-emerald-600" />
                      )}
                    </Button>

                    {/* Toggle role */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleRole(u.id, u.role)}
                      title={u.role === "admin" ? "Retirer admin" : "Promouvoir admin"}
                    >
                      {u.role === "admin" ? (
                        <User className="w-4 h-4 text-slate-500" />
                      ) : (
                        <Crown className="w-4 h-4 text-indigo-600" />
                      )}
                    </Button>

                    {/* Delete */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteUser(u.id, u.email)}
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
